import {
  BREATH_PATTERNS,
  BREATH_PATTERN_IDS,
  type BreathPatternId,
  type BreathStep,
  breathPhaseAt,
  isBreathPatternId,
} from '@goaltree/backend/convex/lib/breathwork'
import { cn } from '@goaltree/ui/lib/utils'
import { ChevronDown, Pause, Play, Square, Volume2, VolumeX } from 'lucide-react'
import { type CSSProperties, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { haptic } from '@/lib/haptics'

const read = (k: string) => {
  try {
    return localStorage.getItem(k)
  } catch {
    return null
  }
}
const write = (k: string, v: string | null) => {
  try {
    if (v === null) localStorage.removeItem(k)
    else localStorage.setItem(k, v)
  } catch {}
}

const patternKey = (habitId: string) => `breathPacer.pattern.${habitId}`
const skipKey = (habitId: string) => `breathPacer.skip.${habitId}`
const CHIME_KEY = 'breathPacer.chime'

export const pacerSkipped = (habitId: string) => read(skipKey(habitId)) === '1'
export const setPacerSkipped = (habitId: string, skip: boolean) =>
  write(skipKey(habitId), skip ? '1' : null)

export const phaseLabelKey = (s: BreathStep) =>
  `${s.kind}${s.side ? (s.side === 'left' ? 'Left' : 'Right') : ''}` as const

const CHIME_HZ = { in: 528, hold: 440, out: 396 } as const

const cue = (kind: BreathStep['kind']) => haptic(kind === 'hold' ? 'light' : 'medium')

function chime(ctx: AudioContext, kind: BreathStep['kind']) {
  try {
    const now = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = 'sine'
    osc.frequency.setValueAtTime(CHIME_HZ[kind], now)
    gain.gain.setValueAtTime(0, now)
    gain.gain.linearRampToValueAtTime(0.05, now + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6)
    osc.start(now)
    osc.stop(now + 0.6)
  } catch {}
}

const clock = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

const MIN_SCALE = 0.42

export function useReducedMotion() {
  const [reduce, setReduce] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const on = () => setReduce(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return reduce
}

export function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return
    let lock: WakeLockSentinel | null = null
    let gone = false
    const take = async () => {
      try {
        if (document.visibilityState !== 'visible') return
        const l = await navigator.wakeLock.request('screen')
        if (gone) void l.release().catch(() => {})
        else lock = l
      } catch {}
    }
    void take()
    document.addEventListener('visibilitychange', take)
    return () => {
      gone = true
      document.removeEventListener('visibilitychange', take)
      void lock?.release().catch(() => {})
    }
  }, [active])
}

const stopEvents = {
  onClick: (e: { stopPropagation: () => void }) => e.stopPropagation(),
  onPointerDown: (e: { stopPropagation: () => void }) => e.stopPropagation(),
  onTouchStart: (e: { stopPropagation: () => void }) => e.stopPropagation(),
  onKeyDown: (e: { stopPropagation: () => void }) => e.stopPropagation(),
}

// Full-screen breathing guide over a running minutes-habit timer. The timer keeps
// the time (pausing only pauses the guide); End stops it and logs as usual.
export function BreathPacer({
  habitId,
  title,
  initialPattern,
  startedAt,
  targetMs,
  accent,
  onMinimize,
  onJustTimer,
  onEnd,
  onTarget,
}: {
  habitId: string
  title: string
  initialPattern: BreathPatternId
  startedAt: number
  // Time this run aims for (a countdown's length or what's left of the day's target).
  targetMs: number
  accent?: string
  onMinimize: () => void
  onJustTimer: () => void
  onEnd: () => void
  // Called once the run reaches its target.
  onTarget?: () => void
}) {
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const [pattern, setPattern] = useState<BreathPatternId>(() => {
    const saved = read(patternKey(habitId))
    return isBreathPatternId(saved) ? saved : initialPattern
  })
  const [paused, setPaused] = useState(false)
  const [chimeOn, setChimeOn] = useState(() => read(CHIME_KEY) !== '0')
  const chimeOnRef = useRef(chimeOn)
  chimeOnRef.current = chimeOn
  const [view, setView] = useState({ index: 0, round: 1, secs: 0 })
  const [now, setNow] = useState(() => Date.now())
  const circle = useRef<HTMLDivElement>(null)
  const audio = useRef<AudioContext | null>(null)
  const clockRef = useRef({ base: 0, resumedAt: performance.now() })
  const targetFired = useRef(false)
  const steps = BREATH_PATTERNS[pattern]
  useWakeLock(!paused)

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(id)
  }, [])

  // A timeout, not the display tick, so the run stops on the target, not after it.
  const onTargetRef = useRef(onTarget)
  onTargetRef.current = onTarget
  useEffect(() => {
    const id = setTimeout(
      () => {
        if (targetFired.current || !onTargetRef.current) return
        targetFired.current = true
        onTargetRef.current()
      },
      Math.max(0, startedAt + targetMs - Date.now()),
    )
    return () => clearTimeout(id)
  }, [startedAt, targetMs])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMinimize()
      else if (e.key === ' ') {
        e.preventDefault()
        setPaused((p) => !p)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onMinimize])

  useEffect(() => {
    if (chimeOnRef.current) {
      try {
        audio.current = new AudioContext()
      } catch {}
    }
    return () => void audio.current?.close().catch(() => {})
  }, [])

  useEffect(() => {
    if (paused) return
    const c = clockRef.current
    c.resumedAt = performance.now()
    let raf = 0
    let last = { index: -1, round: 0, secs: -1 }
    const frame = () => {
      const ph = breathPhaseAt(steps, c.base + performance.now() - c.resumedAt)
      const el = circle.current
      if (el) {
        if (reduce) el.style.opacity = String(0.35 + 0.55 * ph.level)
        else el.style.transform = `scale(${MIN_SCALE + (1 - MIN_SCALE) * ph.level})`
      }
      const secs = Math.ceil(ph.remainingMs / 1000)
      if (ph.index !== last.index || ph.round !== last.round || secs !== last.secs) {
        if (ph.index !== last.index || ph.round !== last.round) {
          cue(ph.step.kind)
          if (chimeOnRef.current && audio.current) chime(audio.current, ph.step.kind)
        }
        last = { index: ph.index, round: ph.round, secs }
        setView(last)
      }
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => {
      cancelAnimationFrame(raf)
      c.base += performance.now() - c.resumedAt
    }
  }, [paused, steps, reduce])

  const pick = (id: BreathPatternId) => {
    if (id === pattern) return
    write(patternKey(habitId), id)
    clockRef.current = { base: 0, resumedAt: performance.now() }
    setPattern(id)
  }
  const toggleChime = () => {
    const on = !chimeOn
    write(CHIME_KEY, on ? null : '0')
    if (on && !audio.current) {
      try {
        audio.current = new AudioContext()
      } catch {}
    }
    setChimeOn(on)
  }

  const step = steps[view.index] ?? steps[0]
  const elapsed = Math.min(targetMs, Math.max(0, now - startedAt))
  const ink = accent ?? 'var(--primary)'

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('breathPacer.title')}
      className="fixed inset-0 z-[100] flex flex-col bg-background text-foreground"
      style={{ '--pacer': ink } as CSSProperties}
      {...stopEvents}
    >
      <div className="mx-auto flex w-full max-w-xl items-center gap-2 px-4 pt-[max(env(safe-area-inset-top),1rem)]">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold">{title}</p>
          <p className="text-[11px] font-medium text-muted-foreground">{t('breathPacer.title')}</p>
        </div>
        <button
          type="button"
          onClick={toggleChime}
          aria-pressed={chimeOn}
          aria-label={t('breathPacer.chime')}
          title={t('breathPacer.chime')}
          className="flex size-10 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted"
        >
          {chimeOn ? <Volume2 className="size-5" /> : <VolumeX className="size-5" />}
        </button>
        <button
          type="button"
          onClick={onMinimize}
          aria-label={t('common.close')}
          className="flex size-10 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted"
        >
          <ChevronDown className="size-5" />
        </button>
      </div>

      <div
        role="radiogroup"
        aria-label={t('breathPacer.pattern')}
        className="mx-auto mt-3 flex max-w-full gap-1.5 overflow-x-auto px-4"
      >
        {BREATH_PATTERN_IDS.map((id) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={id === pattern}
            onClick={() => pick(id)}
            className={cn(
              'shrink-0 cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
              id === pattern
                ? 'border-[var(--pacer)] bg-[color-mix(in_oklab,var(--pacer)_14%,transparent)] text-foreground'
                : 'border-border text-muted-foreground hover:bg-muted',
            )}
          >
            {t(`breathPacer.patterns.${id}`)}
          </button>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4">
        <div className="relative flex aspect-square w-[min(78vw,46vh,360px)] items-center justify-center">
          <div className="absolute inset-0 rounded-full border-2 border-dashed border-foreground/10" />
          <div
            ref={circle}
            aria-hidden
            className="absolute inset-0 rounded-full will-change-transform"
            style={{
              background:
                'radial-gradient(circle at 50% 40%, color-mix(in oklab, var(--pacer) 55%, transparent), color-mix(in oklab, var(--pacer) 22%, transparent) 70%)',
              transform: reduce ? undefined : `scale(${MIN_SCALE})`,
              opacity: reduce ? 0.35 : undefined,
            }}
          />
          {step.side && (
            <span
              aria-hidden
              className={cn(
                'absolute top-1/2 size-3 -translate-y-1/2 rounded-full bg-[var(--pacer)]',
                step.side === 'left' ? '-left-6' : '-right-6',
              )}
            />
          )}
          <div className="relative flex flex-col items-center text-center" aria-live="polite">
            <p className="text-xl font-extrabold tracking-tight sm:text-2xl">
              {paused ? t('breathPacer.paused') : t(`breathPacer.phase.${phaseLabelKey(step)}`)}
            </p>
            <p
              className="mt-1 font-mono text-4xl font-black tabular-nums sm:text-5xl"
              aria-hidden={paused}
            >
              {view.secs}
            </p>
          </div>
        </div>
        <p className="mt-6 font-mono text-sm tabular-nums text-muted-foreground">
          {t('breathPacer.round', { n: view.round })} · {clock(elapsed)} / {clock(targetMs)}
        </p>
      </div>

      <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-3 px-4 pb-[max(env(safe-area-inset-bottom),1.25rem)]">
        <div className="flex w-full gap-2">
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            className="flex h-12 flex-1 cursor-pointer items-center justify-center gap-2 rounded-full border border-border text-sm font-bold transition-colors hover:bg-muted"
          >
            {paused ? <Play className="size-4" /> : <Pause className="size-4" />}
            {t(paused ? 'breathPacer.resume' : 'breathPacer.pause')}
          </button>
          <button
            type="button"
            onClick={onEnd}
            className="flex h-12 flex-1 cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-sm font-extrabold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <Square className="size-3.5" strokeWidth={2.5} />
            {t('breathPacer.end')}
          </button>
        </div>
        <button
          type="button"
          onClick={onJustTimer}
          className="cursor-pointer text-xs font-semibold text-muted-foreground underline-offset-4 hover:underline"
        >
          {t('breathPacer.justTimer')}
        </button>
        <p className="text-center text-[11px] text-muted-foreground/80">
          {t('breathPacer.safety')}
        </p>
      </div>
    </div>,
    document.body,
  )
}
